<script lang="ts">
export default {
  name: 'Combobox',
};
</script>

<script setup lang="ts">
import { computed, ref, watch } from 'vue';
import {
  ComboboxRoot,
  ComboboxInput,
  ComboboxContent,
  ComboboxItem,
  ComboboxItemIndicator,
  useFilter,
  ComboboxAnchor,
  ComboboxTrigger,
  ComboboxPortal,
} from 'reka-ui';
import { Check, ChevronDown } from 'lucide-vue-next';

export interface ComboboxOption {
  label: string;
  value: string;
  disabled?: boolean;
}

const props = withDefaults(
  defineProps<{
    modelValue: string;
    options: ComboboxOption[];
    label?: string;
    placeholder?: string;
    disabled?: boolean;
    inputClass?: string;
    allowFreeText?: boolean;
  }>(),
  { label: '' }
);

const emit = defineEmits<{
  'update:modelValue': [value: string];
  selected: [value: ComboboxOption];
  focusin: [];
  focusout: [];
}>();

// ----------------
// refs and consts
// ----------------
const searchTerm = ref('');
const selectedOption = ref<ComboboxOption | undefined>();
const isOpen = ref(false);

// Use Reka UI's filter utility
const { contains } = useFilter({ sensitivity: 'base' });

// ---------
// computed
// ---------
const filteredOptions = computed(() => {
  if (!searchTerm.value || searchTerm.value.trim() === '') {
    return props.options.filter((option) => !option.disabled).slice(0, 100);
  }
  
  const matchedOptions = props.options.filter((option) => contains(option.label, searchTerm.value) && !option.disabled).slice(0, 100);
  
  // If allowFreeText is enabled and there's search text but no matches, add a custom option
  if (props.allowFreeText && searchTerm.value.trim() && matchedOptions.length === 0) {
    const customOption: ComboboxOption = {
      label: searchTerm.value.trim(),
      value: searchTerm.value.trim(),
      disabled: false
    };
    return [customOption];
  }
  
  return matchedOptions;
});

// --------
// watchers
// --------
watch(
  () => props.modelValue,
  (newValue) => {
    selectedOption.value = props.options.find((option) => option.value === newValue);
  },
  { immediate: true },
);

watch(
  () => props.options,
  () => {
    selectedOption.value = props.options.find((option) => option.value === props.modelValue);
  },
  { immediate: true },
);

// Watch for when the user clears the search term
watch(searchTerm, (newValue, oldValue) => {
  // If the search term is cleared (empty) and we had a selection
  if (newValue === '' && oldValue !== '' && selectedOption.value) {
    // Clear the selection and close the dropdown
    selectedOption.value = undefined;
    emit('update:modelValue', '');
    isOpen.value = false;
  }
  // If the search term is empty and we're opening (from chevron click), close immediately
  else if (newValue === '' && isOpen.value && !selectedOption.value) {
    isOpen.value = false;
  }
});

// --------
// handlers
// --------
function handleSelectionChange(option: ComboboxOption | undefined) {
  if (option) {
    emit('update:modelValue', option.value);
    emit('selected', option);
    isOpen.value = false;
  }
}

function handleOpenChange(open: boolean) {
  isOpen.value = open;
  
  // When opening, set searchTerm to the current label for easier clearing
  if (open && selectedOption.value) {
    searchTerm.value = selectedOption.value.label;
  }
}

function handleInputChange(event: Event) {
  const target = event.target as HTMLInputElement;
  const value = target.value;
  
  // If the input is manually cleared, ensure we clear the selection
  if (value === '' && selectedOption.value) {
    selectedOption.value = undefined;
    emit('update:modelValue', '');
    isOpen.value = false;
  }
}

</script>

<template>
  <div class="tw:form-control tw:w-full">
    <label v-if="label" class="tw:label">
      <span class="tw:label-text tw:text-sm">{{ label }}</span>
    </label>
    <div class="tw:relative tw:w-full">
    <ComboboxRoot
      v-model="selectedOption"
      @update:model-value="handleSelectionChange"
      @update:open="handleOpenChange"
      :disabled="disabled"
      :ignore-filter="true"
      :open="isOpen">
      <ComboboxAnchor>
        <div class="tw:relative">
          <ComboboxInput
            v-model="searchTerm"
            :display-value="(option: ComboboxOption | undefined) => option?.label || ''"
            :placeholder="placeholder || 'Type to search...'"
            :class="['tw:input tw:w-full tw:border-solid', inputClass]"
            @focus="isOpen = true"
            @focusin="emit('focusin')"
            @focusout="emit('focusout')"
            @input="handleInputChange" />
          <ComboboxTrigger class="tw:absolute tw:right-0 tw:top-0 tw:h-full tw:w-10 tw:flex tw:items-center tw:justify-center">
            <ChevronDown
              v-if="!disabled"
              :size="16"
              class="tw:text-gray-500" />
          </ComboboxTrigger>
        </div>
      </ComboboxAnchor>

      <ComboboxPortal>
        <ComboboxContent
          v-if="isOpen && (searchTerm || !selectedOption) && filteredOptions.length > 0"
          class="tw:z-9999 tw:max-h-60 tw:min-w-[200px] tw:overflow-y-auto tw:rounded-md tw:border tw:border-gray-200 tw:bg-white tw:shadow-lg"
          :position="'popper'"
          :side="'bottom'"
          :side-offset="4"
          :align="'start'"
          :avoid-collisions="true">
          <ComboboxItem
            v-for="option in filteredOptions"
            :key="option.value"
            :value="option"
            :disabled="option.disabled"
            class="tw:flex tw:min-h-[40px] tw:cursor-pointer tw:items-center tw:justify-between tw:px-3 tw:py-2 tw:text-sm tw:hover:bg-gray-50 tw:data-[disabled]:cursor-not-allowed tw:data-[highlighted]:bg-gray-50 tw:data-[disabled]:opacity-50">
            <span class="tw:flex-1 tw:text-left">{{ option.label }}</span>
            <ComboboxItemIndicator>
              <Check :size="16" class="tw:shrink-0 tw:stroke-green-600" />
            </ComboboxItemIndicator>
          </ComboboxItem>

          <div
            v-if="filteredOptions.length >= 100"
            class="tw:border-t tw:border-gray-100 tw:px-3 tw:py-2 tw:text-sm tw:font-medium tw:text-gray-500">
            Enter a search term to see more
          </div>

          <div v-if="filteredOptions.length === 0 && searchTerm" class="tw:px-3 tw:py-2 tw:text-sm tw:text-gray-500">
            No matches found
          </div>
        </ComboboxContent>
      </ComboboxPortal>
    </ComboboxRoot>
    </div>
  </div>
</template>
